import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight, X, Sparkle, Check } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * OnboardingTour — first-run guided walkthrough with spotlight effect.
 *
 * UX design:
 * - Cinematic welcome step (no target) personalized with user.name
 * - Spotlight cutout via SVG mask, smoothly animated between targets
 * - Floating tooltip card auto-positioned on best side (right/left/top/bottom)
 * - Step pill, progress dots, Pular / Anterior / Próximo / Concluir
 * - Final celebration step
 * - Keyboard: Esc=skip, ←/→ navigate, Enter=next
 * - Persistence: localStorage `agentus_tour_v11_completed:{userId}` —
 *   escopo POR USUÁRIO. A flag global antiga fazia contas novas no
 *   mesmo navegador nunca verem o tour (bug reportado jul/2026).
 * - Can be re-launched via window event `agentus:replay-tour`
 *
 * Only mounts on /app routes (authenticated shell). Auto-shows once.
 */

const TOUR_FLAG_KEY = "agentus_tour_v12_completed";
const REPLAY_EVENT = "agentus:replay-tour";

// STEPS keep alignment with the current Sidebar.jsx grouping. When the
// sidebar restructures, bump TOUR_FLAG_KEY and update this array — the
// tour breaks silently when `targetTestId` cannot be resolved (we now
// clear the rect on every step transition so it never highlights the
// wrong element from a prior step, but missing targets still mean blank
// steps). The eyebrow strings MUST match the visible group label so the
// user reads "Dia a dia → Conversas" while the sidebar shows "Dia a dia".
// v0.99.45 · Fev/2026 — Steps realinhados ao Sidebar atual (v0.99.11).
// Sidebar real tem: Dashboard, Cérebro, Automações (standalone no topo)
// + grupos "conversas", "impacto", "setup". Os testids anteriores
// (nav-operacao, nav-roi-por-regra, ensureGroup "dia-a-dia", "minha-base",
// "agentus-por-voce") referenciavam uma estrutura antiga que não existe
// mais — por isso o tour mostrava tooltips centralizados sem spotlight.
const STEPS = [
  {
    id: "welcome",
    kind: "center",
    icon: Sparkle,
    title: (name) => `Bem-vindo${name ? `, ${name.split(" ")[0]}` : ""} 👋`,
    body: "Em 2 minutos eu te mostro como o AgentUS funciona — começando pelo coração do sistema: o Cérebro. Se preferir pular, é só clicar no X.",
    cta: "Começar tour",
  },
  {
    id: "sidebar",
    targetTestId: "app-sidebar",
    placement: "right",
    eyebrow: "Navegação",
    title: "Mais enxuto, mais humano",
    body: "A barra lateral é organizada em 5 pilares: Dashboard (visão geral), Cérebro (identidade da IA), Automações (quando ela age), Conversas (dia a dia) e Impacto (resultado).",
    padding: 6,
  },
  {
    id: "wa-pill",
    targetTestId: "topbar-wa-status",
    placement: "bottom",
    eyebrow: "WhatsApp",
    title: "O coração do seu AgentUS",
    body: "Esse selo mostra em tempo real se o WhatsApp do seu negócio está conectado. Clique e cai direto nas integrações.",
    padding: 6,
  },

  // ── Cérebro (ex-Operação, standalone) ────────────────────────────
  {
    id: "nav-cerebro",
    targetTestId: "nav-cerebro",
    placement: "right",
    eyebrow: "Cérebro",
    title: "🧠 É aqui que começa",
    body: "Identidade da IA, horários, serviços, ofertas, cérebro, políticas e boas-vindas — tudo em uma tela só. Cada coisa que você cadastra aqui deixa a IA mais precisa e a impede de inventar preço, horário ou oferta.",
    padding: 4,
  },

  // ── Automações (standalone) ──────────────────────────────────────
  {
    id: "nav-automacoes",
    targetTestId: "nav-automacoes",
    placement: "right",
    eyebrow: "Automações",
    title: "Quando a IA age sozinha",
    body: "Cliente inativo há 60 dias, óleo vencido, lembrete pós-serviço — você define os gatilhos e o agente respeita. Frequência, cooldown e janela de contato ficam sob seu controle.",
    padding: 4,
  },

  // ── Conversas (grupo) ────────────────────────────────────────────
  {
    id: "nav-conversas",
    targetTestId: "nav-conversas",
    placement: "right",
    eyebrow: "Conversas",
    title: "WhatsApp em tempo real",
    body: "Tudo o que o AgentUS conversa com seus clientes vive aqui — com chips inteligentes (aguardando humano, não lidas, VIP). Você pode assumir qualquer conversa num clique.",
    padding: 4,
    ensureGroup: "conversas",
  },
  {
    id: "nav-agenda",
    targetTestId: "nav-agenda",
    placement: "right",
    eyebrow: "Conversas",
    title: "Agenda sem fricção",
    body: "O agente confirma horário sozinho, encaixa na agenda e dispara lembrete. Clique num agendamento para abrir detalhes do cliente e do serviço.",
    padding: 4,
    ensureGroup: "conversas",
  },
  {
    id: "nav-clientes",
    targetTestId: "nav-clientes-e-veiculos",
    placement: "right",
    eyebrow: "Conversas",
    title: "Base de clientes",
    body: "Histórico completo de cada cliente, próximo serviço, status (VIP/inativo), última conversa. Quando a IA cadastra um cliente automaticamente, ele aparece aqui.",
    padding: 4,
    ensureGroup: "conversas",
    optional: true,
  },

  // ── Impacto (grupo) ──────────────────────────────────────────────
  {
    id: "nav-impacto",
    targetTestId: "nav-impacto-do-agentus",
    placement: "right",
    eyebrow: "Impacto",
    title: "Receita gerada pela IA",
    body: "Quanto cada automação trouxe de retorno. Não é vaidade — é receita estimada baseada nos agendamentos e conversões reais.",
    padding: 4,
    ensureGroup: "impacto",
  },
  {
    id: "nav-campanhas",
    targetTestId: "nav-campanhas",
    placement: "right",
    eyebrow: "Impacto",
    title: "Campanhas em massa",
    body: "Reative clientes parados, ofereça promoção, recupere orçamento perdido. Texto ou áudio, com filtro de público. Dispara em segundos.",
    padding: 4,
    ensureGroup: "impacto",
    optional: true,
  },

  // ── Configuração (grupo) ─────────────────────────────────────────
  {
    id: "nav-integracoes",
    targetTestId: "nav-integracoes",
    placement: "right",
    eyebrow: "Configuração",
    title: "WhatsApp e integrações",
    body: "Conecte a Z-API (WhatsApp), ERPs do seu setor e outras ferramentas. Comece pelo WhatsApp — é a integração mais importante.",
    padding: 4,
    ensureGroup: "setup",
  },
  {
    id: "nav-permissoes",
    targetTestId: "nav-permissoes-da-ia",
    placement: "right",
    eyebrow: "Configuração",
    title: "O que ela tem permissão de fazer",
    body: "Mandar mensagem, agendar, oferecer serviço, escalar pra humano. Você liga e desliga cada ação — segurança total.",
    padding: 4,
    ensureGroup: "setup",
    optional: true,
  },

  // ── Setup Wizard inline ──────────────────────────────────────────
  {
    id: "setup-wizard",
    targetTestId: "setup-wizard",
    placement: "bottom",
    eyebrow: "Setup",
    title: "Setup em 5 minutos",
    body: "Esse card mostra o que ainda falta configurar. Cada passo concluído deixa a IA mais precisa. Quando bater 100%, seu primeiro cliente real entra em produção.",
    padding: 8,
    optional: true,
  },

  // ── Suporte ──────────────────────────────────────────────────────
  {
    id: "support",
    targetTestId: "help-fab",
    placement: "left",
    eyebrow: "Suporte",
    title: "Assistente inteligente, 24/7",
    body: "Travou em algo? Esse botão abre um chat com nosso assistente que conhece o sistema inteiro. Pergunta em português, resposta em segundos.",
    padding: 10,
    optional: true,
  },
  {
    id: "done",
    kind: "center",
    icon: Check,
    title: "Pronto 🚀",
    body: "Próximo passo: abra o Cérebro e cadastre identidade, horários e serviços. Com isso a IA já entra em produção respondendo com precisão. Qualquer hora dá pra refazer o tour pelo menu do seu perfil (canto superior direito → Refazer tour).",
    cta: "Ir para o Cérebro",
    ctaPath: "/app/operacao",
  },
];

function readTargetRect(testId) {
  if (!testId) return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function computeTooltipPosition(rect, placement, tooltipSize, viewport, padding = 8) {
  const gap = 18;
  const { tw, th } = tooltipSize;
  const margin = 12;
  let top = 0;
  let left = 0;
  let actual = placement;

  const spaceRight = viewport.w - (rect.left + rect.width);
  const spaceLeft = rect.left;
  const spaceBelow = viewport.h - (rect.top + rect.height);
  const spaceAbove = rect.top;

  // Auto-adjust placement if not enough space
  if (placement === "right" && spaceRight < tw + gap + margin) actual = spaceLeft > tw + gap + margin ? "left" : "bottom";
  if (placement === "left" && spaceLeft < tw + gap + margin) actual = spaceRight > tw + gap + margin ? "right" : "bottom";
  if (placement === "bottom" && spaceBelow < th + gap + margin) actual = spaceAbove > th + gap + margin ? "top" : "right";
  if (placement === "top" && spaceAbove < th + gap + margin) actual = spaceBelow > th + gap + margin ? "bottom" : "right";

  if (actual === "right") {
    left = rect.left + rect.width + gap;
    top = rect.top + rect.height / 2 - th / 2;
  } else if (actual === "left") {
    left = rect.left - tw - gap;
    top = rect.top + rect.height / 2 - th / 2;
  } else if (actual === "bottom") {
    left = rect.left + rect.width / 2 - tw / 2;
    top = rect.top + rect.height + gap;
  } else {
    // top
    left = rect.left + rect.width / 2 - tw / 2;
    top = rect.top - th - gap;
  }

  // Clamp to viewport
  left = Math.max(margin, Math.min(left, viewport.w - tw - margin));
  top = Math.max(margin, Math.min(top, viewport.h - th - margin));

  return { top, left, placement: actual };
}

export default function OnboardingTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Only run inside the authenticated shell.
  const isInsideApp = location.pathname.startsWith("/app");

  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, placement: "right" });
  const [tooltipSize, setTooltipSize] = useState({ tw: 380, th: 220 });
  const [enter, setEnter] = useState(false);
  const tooltipRef = useRef(null);

  // Flag por usuário — cada conta nova vê o tour, mesmo em navegador
  // que já rodou o AgentUS com outra conta.
  const flagKey = user?.id ? `${TOUR_FLAG_KEY}:${user.id}` : TOUR_FLAG_KEY;

  // Auto-launch on first authenticated landing on dashboard
  useEffect(() => {
    if (!isInsideApp || !user) return;
    const done = typeof window !== "undefined" && localStorage.getItem(flagKey);
    if (done) return;
    // v0.99.45 · Fev/2026 — orquestração de overlays no first login.
    // Antes abríamos em 700ms sem checar nada; o resultado era
    // tour aparecendo ATRÁS do WhatsNewModal e do TermsReacceptGate,
    // ou junto com o SetupWizard — menu "sumia", cards não carregavam
    // em tempo (bug reportado). Agora esperamos o DOM estar limpo
    // antes de disparar.
    let cancelled = false;
    const tryLaunch = (attempt = 0) => {
      if (cancelled) return;
      // Modais bloqueantes abertos? Aguarde-os.
      const blockers = document.querySelectorAll(
        '[data-testid="terms-reaccept-modal"], [data-testid="whatsnew-modal"], [data-testid="no-credits-modal"]',
      );
      if (blockers.length > 0) {
        // Reagenda — até 30 tentativas (30 * 400ms = 12s), senão desiste.
        if (attempt < 30) {
          setTimeout(() => tryLaunch(attempt + 1), 400);
        }
        return;
      }
      // Sidebar precisa estar montada (resolve "menu não aparecia").
      const sidebar = document.querySelector('[data-testid="app-sidebar"]');
      if (!sidebar) {
        if (attempt < 20) {
          setTimeout(() => tryLaunch(attempt + 1), 300);
        }
        return;
      }
      // Rota correta?
      if (location.pathname !== "/app") navigate("/app", { replace: false });
      setActive(true);
      setStepIdx(0);
    };
    const t = setTimeout(() => tryLaunch(), 900);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsideApp, user]);

  // External replay trigger
  useEffect(() => {
    const handler = () => {
      if (location.pathname !== "/app") navigate("/app");
      setStepIdx(0);
      setActive(true);
    };
    window.addEventListener(REPLAY_EVENT, handler);
    return () => window.removeEventListener(REPLAY_EVENT, handler);
  }, [location.pathname, navigate]);

  const step = STEPS[stepIdx];
  const totalSteps = STEPS.length;

  // Resolve the current target (skip optional steps whose target is absent).
  // Tour steps for sidebar items live inside collapsible groups; when the
  // step declares `ensureGroup`, we click the group header to expand it
  // FIRST, then poll for the target. This keeps the cinematic walkthrough
  // smooth even though groups default to collapsed.
  useLayoutEffect(() => {
    if (!active || !step) return;
    // Always clear the previously-highlighted rect FIRST. Without this,
    // when the new step's `targetTestId` fails to resolve (e.g. the user
    // is on a page where the element isn't mounted, or the testid was
    // renamed in a sidebar refactor), the tooltip would inherit the
    // previous step's rect and the spotlight would draw on the wrong
    // element — that's the "destaca o plano ativo errado" bug the
    // operator reported.
    setRect(null);
    setEnter(false);

    if (step.kind === "center") {
      requestAnimationFrame(() => setEnter(true));
      return;
    }

    if (step.ensureGroup) {
      const header = document.querySelector(`[data-testid="sidebar-group-${step.ensureGroup}"]`);
      // Click only when collapsed. The header carries aria-expanded so we
      // trust that flag instead of guessing from DOM children (other groups
      // may already have rendered nav items).
      if (header && header.getAttribute("aria-expanded") === "false") {
        header.click();
      }
    }

    let attempts = 0;
    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const r = readTargetRect(step.targetTestId);
      if (r) {
        setRect(r);
        requestAnimationFrame(() => setEnter(true));
        return;
      }
      if (attempts++ < 12) {
        setTimeout(tryFind, 120);
      } else if (step.optional) {
        // Skip optional step gracefully
        setStepIdx((i) => Math.min(i + 1, totalSteps - 1));
      } else {
        // Worst case: render a centered tooltip without spotlight.
        // `rect` stays null (we already cleared it above) so the user
        // never sees a highlight on the wrong element. The tooltip
        // body still explains the feature.
        setEnter(true);
      }
    };
    tryFind();
    return () => { cancelled = true; };
  }, [active, stepIdx, step, totalSteps]);

  // Recompute position on rect or window resize
  const recompute = useCallback(() => {
    if (!active || !step) return;
    const viewport = { w: window.innerWidth, h: window.innerHeight };
    if (step.kind === "center" || !rect) {
      setTooltipPos({
        top: viewport.h / 2 - tooltipSize.th / 2,
        left: viewport.w / 2 - tooltipSize.tw / 2,
        placement: "center",
      });
      return;
    }
    setTooltipPos(computeTooltipPosition(rect, step.placement, tooltipSize, viewport, step.padding));
  }, [active, step, rect, tooltipSize]);

  useEffect(() => { recompute(); }, [recompute]);

  // Measure tooltip after it renders
  useEffect(() => {
    if (!active) return;
    const el = tooltipRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (Math.abs(r.width - tooltipSize.tw) > 1 || Math.abs(r.height - tooltipSize.th) > 1) {
      setTooltipSize({ tw: r.width, th: r.height });
    }
  }, [active, stepIdx, enter, tooltipSize.tw, tooltipSize.th]);

  // Listen to resize/scroll to re-anchor
  useEffect(() => {
    if (!active) return;
    const onChange = () => {
      if (step?.kind === "center") {
        recompute();
        return;
      }
      const r = readTargetRect(step?.targetTestId);
      if (r) setRect(r);
      recompute();
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [active, step, recompute]);

  const finish = useCallback((targetPath = null) => {
    setActive(false);
    try { localStorage.setItem(flagKey, "1"); } catch {}
    if (targetPath) {
      // Small delay so the overlay finishes its fade-out before route change.
      setTimeout(() => navigate(targetPath), 80);
    }
  }, [navigate, flagKey]);

  const next = useCallback(() => {
    if (stepIdx >= totalSteps - 1) {
      // Final step may carry a `ctaPath` — close + navigate.
      return finish(step?.ctaPath || null);
    }
    setStepIdx((i) => i + 1);
  }, [stepIdx, totalSteps, finish, step]);

  const prev = useCallback(() => {
    if (stepIdx === 0) return;
    setStepIdx((i) => Math.max(0, i - 1));
  }, [stepIdx]);

  // Keyboard
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(); }
      else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, next, prev, finish]);

  // SVG mask cutout for the spotlight
  const spotlight = useMemo(() => {
    if (!active || !rect || step?.kind === "center") return null;
    const pad = step?.padding ?? 8;
    const x = Math.max(0, rect.left - pad);
    const y = Math.max(0, rect.top - pad);
    const w = rect.width + pad * 2;
    const h = rect.height + pad * 2;
    const r = 12;
    return { x, y, w, h, r };
  }, [active, rect, step]);

  if (!active || !isInsideApp) return null;

  const Icon = step?.icon;
  const isCenter = step?.kind === "center";
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === totalSteps - 1;

  return (
    <div
      className="fixed inset-0 z-[60]"
      data-testid="onboarding-tour-overlay"
      aria-live="polite"
      role="dialog"
      aria-label="Tour de boas-vindas do AgentUS"
    >
      {/* Backdrop with SVG mask */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        style={{ transition: "opacity 280ms ease-out", opacity: enter ? 1 : 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.x}
                y={spotlight.y}
                width={spotlight.w}
                height={spotlight.h}
                rx={spotlight.r}
                ry={spotlight.r}
                fill="black"
                style={{ transition: "all 360ms cubic-bezier(0.4, 0, 0.2, 1)" }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(2, 6, 23, 0.62)"
          mask="url(#tour-mask)"
        />
        {/* Animated ring around the spotlight */}
        {spotlight && (
          <rect
            x={spotlight.x}
            y={spotlight.y}
            width={spotlight.w}
            height={spotlight.h}
            rx={spotlight.r}
            ry={spotlight.r}
            fill="none"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="2"
            strokeDasharray="6 6"
            style={{ transition: "all 360ms cubic-bezier(0.4, 0, 0.2, 1)" }}
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="-24"
              dur="1.4s"
              repeatCount="indefinite"
            />
          </rect>
        )}
      </svg>

      {/* Tooltip / center modal */}
      <div
        ref={tooltipRef}
        data-testid={`tour-step-${step?.id}`}
        style={{
          position: "fixed",
          top: tooltipPos.top,
          left: tooltipPos.left,
          maxWidth: isCenter ? 460 : 380,
          width: "calc(100vw - 32px)",
          transition: "top 360ms cubic-bezier(0.4, 0, 0.2, 1), left 360ms cubic-bezier(0.4, 0, 0.2, 1), opacity 240ms ease-out, transform 240ms ease-out",
          opacity: enter ? 1 : 0,
          transform: enter ? "scale(1)" : "scale(0.96)",
        }}
        className={`bg-white border border-slate-200 shadow-[0_24px_60px_-12px_rgba(2,6,23,0.45)] ${isCenter ? "rounded-2xl p-7" : "rounded-xl p-5"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: step counter + close */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isCenter && Icon && (
              <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                <Icon size={16} weight="regular" />
              </div>
            )}
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">
              {step?.eyebrow || `Passo ${stepIdx + 1} de ${totalSteps}`}
            </span>
          </div>
          <button
            onClick={() => finish()}
            data-testid="tour-close-btn"
            aria-label="Pular tour"
            className="text-slate-400 hover:text-slate-900 transition-colors p-1 -mr-1"
          >
            <X size={14} weight="regular" />
          </button>
        </div>

        {/* Body */}
        <h3 className={`font-heading font-semibold text-slate-900 tracking-tight ${isCenter ? "text-2xl" : "text-lg"}`}>
          {typeof step?.title === "function" ? step.title(user?.name) : step?.title}
        </h3>
        <p className={`text-slate-600 leading-relaxed mt-2 ${isCenter ? "text-[15px]" : "text-sm"}`}>
          {step?.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mt-5">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              data-testid={`tour-progress-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === stepIdx
                  ? "w-6 bg-slate-900"
                  : i < stepIdx
                  ? "w-1.5 bg-slate-400"
                  : "w-1.5 bg-slate-200"
              }`}
            />
          ))}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between mt-5">
          {isFirst ? (
            <button
              onClick={() => finish()}
              data-testid="tour-skip-btn"
              className="text-[12px] text-slate-500 hover:text-slate-900 transition-colors tracking-tight"
            >
              Pular tour
            </button>
          ) : (
            <button
              onClick={prev}
              data-testid="tour-prev-btn"
              className="inline-flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-900 transition-colors tracking-tight"
            >
              <CaretLeft size={12} weight="bold" /> Anterior
            </button>
          )}
          <button
            onClick={next}
            data-testid="tour-next-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors tracking-tight shadow-[0_4px_12px_-2px_rgba(2,6,23,0.35)]"
          >
            {isLast ? (step?.cta || "Concluir") : (step?.cta || "Próximo")}
            {!isLast && <CaretRight size={12} weight="bold" />}
            {isLast && <Check size={13} weight="bold" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Public API: trigger replay from anywhere in the app. */
export function replayOnboardingTour() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(TOUR_FLAG_KEY))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  window.dispatchEvent(new Event(REPLAY_EVENT));
}
